/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { dashboardAPI, ticketsAPI } from '../../lib/api';
import { formatDate, formatRelativeTime, TICKET_STATUS, TICKET_TYPES } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import {
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Megaphone,
  Plus,
  Lightbulb,
  MessageSquare,
  Star,
  Loader2,
  User,
  Home,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, description, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <Card className="card-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const VatandasDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Vatandas Paneli');
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, announcementsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentTickets(5),
        dashboardAPI.getRecentAnnouncements(5),
      ]);
      
      setStats(statsRes.data);
      setRecentTickets(ticketsRes.data);
      setRecentAnnouncements(announcementsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatusBadge = (status) => {
    const statusInfo = TICKET_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge className={`${colorClasses[statusInfo.color]} border`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vatandas-dashboard">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <User className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Hoş Geldiniz, {user?.name}!</h1>
            <p className="text-blue-100 mt-1">
              Kent Konseyi Dijital Katılım Platformu'na hoş geldiniz. Taleplerınızı buradan takip edebilirsiniz.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Toplam Talebim"
          value={stats?.total_tickets || 0}
          icon={FileText}
          color="blue"
        />
        <StatCard
          title="Bekleyen Talepler"
          value={stats?.open_tickets || 0}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Çözülen Talepler"
          value={stats?.resolved_tickets || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Ort. Çözüm Süresi"
          value={`${stats?.avg_resolution_days || 0} gün`}
          icon={AlertCircle}
          color="purple"
        />
      </div>

      {/* Quick Actions */}
      <Card className="card-shadow border-amber-200 bg-amber-50/30" data-testid="project-proposal-bar">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Proje fikriniz mi var?</h3>
              <p className="text-slate-600">Sehriniz icin yeni bir proje onerisi olusturun.</p>
            </div>
            <Link to="/panel/talepler/proje-onerisi">
              <Button className="bg-amber-600 hover:bg-amber-700" data-testid="project-proposal-btn">
                <Lightbulb className="w-4 h-4 mr-2" />
                Proje Onerisi
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card className="card-shadow border-blue-200 bg-blue-50/30" data-testid="new-ticket-bar">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Yeni bir talebiniz mi var?</h3>
              <p className="text-slate-600">Talep, şikayet veya önerinizi hemen iletin.</p>
            </div>
            <Link to="/panel/talepler/yeni">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Talep Oluştur
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card className="card-shadow border-purple-200 bg-purple-50/30" data-testid="surveys-bar">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Anketler</h3>
              <p className="text-slate-600">Aktif anketlere katilarak sehrinizin gelecegine yon verin.</p>
            </div>
            <Link to="/panel/anketler">
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="surveys-nav-btn">
                <Star className="w-4 h-4 mr-2" />
                Anketlere Git
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Recent Tickets */}
        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Son Taleplerim</CardTitle>
              <CardDescription>Son oluşturduğunuz talepler</CardDescription>
            </div>
            <Link to="/panel/talepler">
              <Button variant="ghost" size="sm">
                Tümü <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentTickets.length > 0 ? (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/panel/talepler/${ticket.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{ticket.subject}</p>
                      <p className="text-xs text-slate-500">{ticket.ticket_no} • {formatRelativeTime(ticket.created_at)}</p>
                    </div>
                    <div className="ml-4">{getStatusBadge(ticket.status)}</div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Henüz talep oluşturmadınız</p>
                <Link to="/panel/talepler/yeni">
                  <Button variant="outline" className="mt-3">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Talebinizi Oluşturun
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Duyurular</CardTitle>
              <CardDescription>Kent Konseyi'nden haberler</CardDescription>
            </div>
            <Link to="/panel/duyurular">
              <Button variant="ghost" size="sm">
                Tümü <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentAnnouncements.length > 0 ? (
              <div className="space-y-3">
                {recentAnnouncements.map((announcement) => (
                  <Link
                    key={announcement.id}
                    to={`/panel/duyurular/${announcement.id}`}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                  >
                    <div className="p-2 rounded-lg bg-amber-50 flex-shrink-0">
                      <Megaphone className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 line-clamp-1">{announcement.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatRelativeTime(announcement.created_at)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Henüz duyuru bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Talep & Şikayet</h3>
            <p className="text-sm text-slate-500 mt-2">
              Şehrinizle ilgili talep ve şikayetlerinizi iletin
            </p>
          </CardContent>
        </Card>
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Star className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Öneriler</h3>
            <p className="text-sm text-slate-500 mt-2">
              Şehrinizi daha güzel yapmak için önerilerinizi paylaşın
            </p>
          </CardContent>
        </Card>
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Katılım</h3>
            <p className="text-sm text-slate-500 mt-2">
              Kent Konseyi toplantılarına katılın ve sesinizi duyurun
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VatandasDashboard;
